import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import './evacuations.scss';

import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useUpdatePeriod } from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import { getEvacuationsData } from '../reducer';
import Loader from '../../../components/base/Loader/Loader';
import { loadEvacuationsList } from '../actions';
import YMap from '../../../components/base/Map/YMap';
import EvacuationsList from './EvacuationsList';
import { errorNotification } from '../../Notifications/notificationDecorator';
import {
  filterEvacuationApplications,
  getDefaultEvacuationMapCenter,
  getEvacuationApplicationBalloonData,
  getEvacuationApplicationId,
  getEvacuationApplicationMapBalloonData,
  getEvacuationApplicationMapCoordinates,
  getEvacuationApplicationPlacemarkOptions,
  getEvacuationApplicationsBounds,
  getEvacuationApplicationsWithCoordinates,
} from '../evacuationApplications';

export default function EvacuationsPage() {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [selected, setSelected] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [search, setSearch] = useState('');
  const balloonTimerRef = useRef(null);
  const balloonRequestRef = useRef(0);
  const { updatePeriod } = useSelector(getSystemData);
  const {
    evacuationsList,
    loadingEvacuationsList,
  } = useSelector(getEvacuationsData);

  const filteredApplications = useMemo(
    () => filterEvacuationApplications(evacuationsList, search),
    [evacuationsList, search],
  );
  const applicationsWithCoordinates = useMemo(
    () => getEvacuationApplicationsWithCoordinates(filteredApplications),
    [filteredApplications],
  );
  const selectedElement = filteredApplications.find(item => getEvacuationApplicationId(item) === selected);
  const skippedWithoutCoordinates = filteredApplications.length - applicationsWithCoordinates.length;

  const loadData = () => {
    const requestData = {
      filter: {},
    };
    const p1 = dispatch(loadEvacuationsList(requestData)).catch(() => {
      errorNotification('Загрузка эвакуаций', 'Не удалось загрузить заявки');
    });
    return Promise.all([p1]);
  };

  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);

  useEffect(() => {
    if (selected && !selectedElement) {
      setSelected(null);
    }
  }, [selected, selectedElement]);

  const clearPendingBalloon = useCallback(() => {
    if (balloonTimerRef.current) {
      window.clearTimeout(balloonTimerRef.current);
      balloonTimerRef.current = null;
    }
  }, []);

  const closeMapBalloon = useCallback(() => {
    clearPendingBalloon();
    if (mapInstance?.balloon && typeof mapInstance.balloon.close === 'function') {
      mapInstance.balloon.close();
    }
  }, [clearPendingBalloon, mapInstance]);

  const openApplicationBalloon = useCallback((application, options = {}) => {
    const coordinates = getEvacuationApplicationMapCoordinates(application);
    if (
      !coordinates
      || !mapInstance
      || typeof mapInstance.setCenter !== 'function'
      || !mapInstance.balloon
      || typeof mapInstance.balloon.open !== 'function'
    ) {
      return;
    }

    const requestId = balloonRequestRef.current + 1;
    balloonRequestRef.current = requestId;
    clearPendingBalloon();

    if (typeof mapInstance.balloon.close === 'function') {
      mapInstance.balloon.close();
    }

    const zoom = options.preserveZoom && typeof mapInstance.getZoom === 'function'
      ? mapInstance.getZoom()
      : 16;
    const balloonData = getEvacuationApplicationMapBalloonData(application);
    const scheduleOpen = () => {
      clearPendingBalloon();
      balloonTimerRef.current = window.setTimeout(() => {
        if (
          balloonRequestRef.current !== requestId
          || !mapInstance?.balloon
          || typeof mapInstance.balloon.open !== 'function'
        ) {
          return;
        }
        mapInstance.balloon.open(coordinates, balloonData);
      }, 0);
    };

    const centerResult = mapInstance.setCenter(coordinates, zoom, {
      checkZoomRange: true,
    });

    if (centerResult && typeof centerResult.then === 'function') {
      centerResult.then(scheduleOpen, scheduleOpen);
    } else {
      scheduleOpen();
    }
  }, [clearPendingBalloon, mapInstance]);

  useEffect(() => () => {
    balloonRequestRef.current += 1;
    clearPendingBalloon();
  }, [clearPendingBalloon, mapInstance]);

  const createClusterer = list => {
    if (!window.ymaps || !list?.length) {
      return null;
    }
    const clusterer = new window.ymaps.Clusterer({
      clusterIconLayout: 'default#pieChart',
      clusterIconPieChartRadius: 25,
      clusterIconPieChartCoreRadius: 10,
      clusterIconPieChartStrokeWidth: 2,
      clusterDisableClickZoom: true,
      clusterHideIconOnBalloonOpen: false,
      geoObjectHideIconOnBalloonOpen: false,
    });
    const geoObjects = list.map(application => {
      const placemark = new window.ymaps.Placemark(
        getEvacuationApplicationMapCoordinates(application),
        getEvacuationApplicationBalloonData(application),
        getEvacuationApplicationPlacemarkOptions(application),
      );
      if (placemark.events && typeof placemark.events.add === 'function') {
        placemark.events.add('click', () => openApplicationBalloon(application, { preserveZoom: true }));
      }
      return placemark;
    });
    clusterer.options.set({
      gridSize: 80,
      clusterDisableClickZoom: true,
    });
    clusterer.add(geoObjects);
    return clusterer;
  };

  useEffect(() => {
    let clusterer = null;
    const canUseMapObjects = mapInstance?.geoObjects && typeof mapInstance.geoObjects.add === 'function';
    if (canUseMapObjects && applicationsWithCoordinates.length) {
      clusterer = createClusterer(applicationsWithCoordinates);
      if (clusterer) {
        mapInstance.geoObjects.add(clusterer);
        if (applicationsWithCoordinates.length === 1 && typeof mapInstance.setCenter === 'function') {
          mapInstance.setCenter(getEvacuationApplicationMapCoordinates(applicationsWithCoordinates[0]), 15, {
            checkZoomRange: true,
          });
        } else {
          const bounds = getEvacuationApplicationsBounds(applicationsWithCoordinates);
          if (bounds && typeof mapInstance.setBounds === 'function') {
            mapInstance.setBounds(bounds, {
              checkZoomRange: true,
              zoomMargin: 40,
            });
          }
        }
      }
    } else if (mapInstance && typeof mapInstance.setCenter === 'function') {
      mapInstance.setCenter(getDefaultEvacuationMapCenter(), 11, {
        checkZoomRange: true,
      });
    }
    return () => {
      if (mapInstance?.geoObjects && typeof mapInstance.geoObjects.remove === 'function' && clusterer) {
        mapInstance.geoObjects.remove(clusterer);
      }
    };
  }, [applicationsWithCoordinates, mapInstance, openApplicationBalloon]);

  useEffect(() => {
    if (!selectedElement) {
      closeMapBalloon();
      return;
    }
    if (!getEvacuationApplicationMapCoordinates(selectedElement)) {
      closeMapBalloon();
      return;
    }
    openApplicationBalloon(selectedElement);
  }, [selected, selectedElement, closeMapBalloon, openApplicationBalloon]);

  const handleSelect = item => () => {
    const id = getEvacuationApplicationId(item);
    if (!id) {
      return;
    }
    setSelected(current => (current === id ? null : id));
  };

  return (
    <div className="page application-page">
      <Loader loading={loadingEvacuationsList} />
      <div className="page-content-wrapper">
        <div className="evacuations-columns">
          <EvacuationsList
            handleSelect={handleSelect}
            selected={selected}
            evacuationsList={filteredApplications}
            searchValue={search}
            onSearchChange={setSearch}
            totalCount={evacuationsList.length}
            markersCount={applicationsWithCoordinates.length}
            skippedWithoutCoordinates={skippedWithoutCoordinates}
          />
          <div className="map-columns card">
            <YMap
              className="evacuations-map"
              setMapInstance={i => setMapInstance(i)}
            />
            {!loadingEvacuationsList && filteredApplications.length && !applicationsWithCoordinates.length ? (
              <div className="evacuations-map-state">
                <div className="fw-semibold">Нет заявок с координатами</div>
                <div className="small">Записи без валидной геопозиции пропущены на карте.</div>
              </div>
            ) : null}
            {!loadingEvacuationsList && !filteredApplications.length ? (
              <div className="evacuations-map-state">
                <div className="fw-semibold">{formatMessage({ id: 'no.data', defaultMessage: 'Нет данных' })}</div>
                <div className="small">Нет заявок для отображения.</div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
